import React, { useState, useMemo } from "react";
import { Plus, RefreshCcw, ChevronDown, ChevronUp, Layers, Search, X, Save } from "lucide-react";
import { Button } from "components/ui/button";

interface TemplateNodeProps {
  node: any;
  templates: any[];
  isOpen: boolean;
  isRefreshing?: boolean;
  onToggleAccordion: () => void;
  onDragStart?: (event: React.DragEvent, nodeData: any) => void;
  onAddNode: (nodeData: any) => void;
  onRefreshTemplates: () => void;
  onSaveTemplate: () => void;
}

const TemplateNode: React.FC<TemplateNodeProps> = ({
  node, templates, isOpen, isRefreshing = false,
  onToggleAccordion, onDragStart, onAddNode, onRefreshTemplates, onSaveTemplate,
}) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () => search.trim()
      ? templates.filter(t => t.name?.toLowerCase().includes(search.toLowerCase()))
      : templates,
    [templates, search]
  );

  return (
    <div className="border border-line dark:border-white/[0.08] rounded-xl overflow-hidden shadow-sm bg-surface dark:bg-dark-800">

      {/* Header */}
      <div
        onClick={onToggleAccordion}
        className="flex justify-between items-center px-3 py-2.5 cursor-pointer
                   bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.07]
                   border-b border-line dark:border-white/[0.06] transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200 text-sm">
          <Layers className="h-4 w-4 text-sky-500 dark:text-sky-400" />
          <span>{node.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            onClick={e => { e.stopPropagation(); onSaveTemplate(); }}
            variant="ghost" size="icon"
            className="text-content-subtle dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 h-7 w-7"
            title="Save as Template"
          >
            <Save className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={e => { e.stopPropagation(); onRefreshTemplates(); }}
            variant="ghost" size="icon"
            className="text-content-subtle dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 h-7 w-7"
            title="Refresh Templates"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
          {isOpen ? <ChevronUp className="h-4 w-4 text-content-subtle dark:text-gray-200" />
                  : <ChevronDown className="h-4 w-4 text-content-subtle dark:text-gray-200" />}
        </div>
      </div>

      {/* Body */}
      {isOpen && (
        <div className="p-2 space-y-1.5">

          {/* Search + count */}
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-content-subtle dark:text-gray-600 pointer-events-none" />
              <input
                type="text" placeholder="Search…" value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-6 pr-5 py-1 text-[11px] rounded-md
                           bg-gray-50 dark:bg-white/[0.05]
                           border border-line dark:border-white/[0.08]
                           text-gray-700 dark:text-gray-300
                           placeholder-gray-400 dark:placeholder-gray-600
                           focus:outline-none focus:ring-1 focus:ring-sky-400/60
                           transition-colors"
              />
              {search && (
                <button onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary dark:hover:text-gray-200">
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
            {!isRefreshing && (
              <span className="flex-shrink-0 text-[9.5px] text-content-subtle dark:text-gray-600 tabular-nums">
                {filtered.length}/{templates.length}
              </span>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto rounded-md border border-line-subtle dark:border-white/[0.06]
                          [&::-webkit-scrollbar]:w-[3px]
                          [&::-webkit-scrollbar-thumb]:rounded-full
                          [&::-webkit-scrollbar-thumb]:bg-sky-300/40
                          dark:[&::-webkit-scrollbar-thumb]:bg-sky-600/40"
               style={{ maxHeight: 168 }}>
            {isRefreshing ? (
              (["72%", "58%", "85%", "65%"] as const).map((w, i) => (
                <div key={i} className="sidebar-skeleton-row border-b border-line-subtle dark:border-white/[0.04] last:border-0">
                  <div className="sidebar-skeleton-line" style={{ width: w }} />
                  <div className="sidebar-skeleton-dot" />
                </div>
              ))
            ) : filtered.length > 0 ? (
              filtered.map((t, idx) => (
                <div
                  key={t.id || t.name}
                  draggable
                  onDragStart={e => {
                    const nodeData = { type: "template", name: t.name };
                    e.dataTransfer.setData("application/json", JSON.stringify(nodeData));
                    onDragStart?.(e, nodeData);
                  }}
                  className={`group flex items-center justify-between px-2 py-[5px]
                             hover:bg-sky-50 dark:hover:bg-sky-900/10
                             cursor-grab active:cursor-grabbing transition-colors
                             ${idx < filtered.length - 1 ? "border-b border-line-subtle dark:border-white/[0.04]" : ""}`}
                >
                  <span className="text-micro font-medium text-content-secondary dark:text-gray-300 truncate leading-none">
                    {t.name}
                  </span>
                  <button
                    onClick={() => onAddNode({ type: "template", name: t.name })}
                    title="Add to canvas"
                    className="flex-shrink-0 ml-1.5 w-[18px] h-[18px] rounded-full flex items-center justify-center
                               opacity-80 group-hover:opacity-100
                               bg-gray-100 dark:bg-white/[0.08]
                               group-hover:bg-sky-500 dark:group-hover:bg-sky-600
                               text-content-muted dark:text-gray-400 group-hover:text-white transition-all"
                  >
                    <Plus className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[11px] text-content-subtle dark:text-gray-600 italic text-center py-4">
                {search ? `No results for "${search}"` : "No templates found."}
              </p>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default TemplateNode;
